// 📁 File: /pages/account/ManageTeam.jsx
import React, { useEffect, useState, useContext } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Sidebar from '../../components/DashboardSidebar';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { AuthContext } from '../../context/AuthContext';
import { Menu, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_API_BASE;

export default function ManageTeam() {
  const auth = useContext(AuthContext);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLimit, setTeamLimit] = useState(1);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lang, setLang] = useState(auth?.user?.language || 'en');
  const [removalLoading, setRemovalLoading] = useState(null);
  const [resendLoading, setResendLoading] = useState(null);
  const token = auth?.user?.token;

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const res = await fetchWithAuth(`${API}/api/teamAccount/team`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      setTeamMembers(data.members);
      setTeamLimit(data.limit);
    } catch (err) {
      toast.error('Failed to load team');
    }
  };

  const handleInvite = async () => {
    if (!newMemberEmail) return;
    if (teamMembers.length >= teamLimit) {
      toast.error('Team limit reached.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/api/teamAccount/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email: newMemberEmail, name: newMemberName })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Invitation sent');
        setNewMemberEmail('');
        setNewMemberName('');
        loadTeam();
      } else {
        toast.error(data.error || 'Failed to invite');
      }
    } catch (err) {
      toast.error('Error inviting member');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (memberId) => {
    const confirmed = window.confirm('Remove this member?');
    if (!confirmed) return;
    setRemovalLoading(memberId);
    try {
      const res = await fetchWithAuth(`${API}/api/teamAccount/team/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      if (res.ok) {
        toast.success('Member removed');
        loadTeam();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to remove');
      }
    } catch (err) {
      toast.error('Error removing member');
    } finally {
      setRemovalLoading(null);
    }
  };

  const handleResend = async (email) => {
    setResendLoading(email);
    try {
      const res = await fetchWithAuth(`${API}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        toast.success('Verification email resent');
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to resend');
      }
    } catch (err) {
      toast.error('Error resending email');
    } finally {
      setResendLoading(null);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className={`${sidebarOpen ? 'block' : 'hidden'} fixed md:relative md:block z-40 w-64`}>
        <Sidebar
          currentLang={lang}
          activeSection="account"
          onLangChange={setLang}
          onCloseMobile={() => setSidebarOpen(false)}
        />
      </div>

      <div className="flex-1 p-6">
        <button onClick={() => setSidebarOpen(true)} className="block md:hidden mb-4">
          <Menu className="w-6 h-6 text-gray-700" />
        </button>

        <h2 className="text-3xl font-semibold mb-6 text-gray-800">Manage Teams</h2>
        <p className="mb-2">{teamMembers.length} of {teamLimit} seats used (including you)</p>

        <div className="flex gap-2 mb-4">
          <Input
            value={newMemberName}
            onChange={e => setNewMemberName(e.target.value)}
            placeholder="Name"
          />
          <Input
            value={newMemberEmail}
            onChange={e => setNewMemberEmail(e.target.value)}
            placeholder="Email"
          />
          <Button disabled={loading} onClick={handleInvite}>
            {loading ? <Loader2 className="animate-spin w-4 h-4 mr-2" /> : null} Invite
          </Button>
        </div>

        {teamMembers.map(member => (
          <Card key={member._id} className="mb-2">
            <CardContent className="flex items-center justify-between">
              <div>
                <p className="font-medium">{member.name || member.email}</p>
                <p className="text-sm text-gray-500">{member.email} — {member.status}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  onClick={() => handleRemove(member._id)}
                  disabled={removalLoading === member._id}
                >
                  {removalLoading === member._id ? <Loader2 className="animate-spin w-4 h-4 mr-2" /> : null} Remove
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleResend(member.email)}
                  disabled={resendLoading === member.email}
                >
                  {resendLoading === member.email ? <Loader2 className="animate-spin w-4 h-4 mr-2" /> : null} Resend Verification Email
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
